import { connect } from 'react-redux';
import { RootState } from '@iwProvider/configureStore';
import { getTopUpStatus } from '../NewOutstandingView.helpers';
import { TProductType } from '../../../dashboard/Dashboard.types';
import { NotYetTopUpEligibleAlert } from './Alerts/NotYetTopUpEligibleAlert';
import { TopUpPendingAlert } from './Alerts/TopUpPendingAlert';
import { TopUpEligibleAlert } from './Alerts/TopUpEligibleAlert';
import { TopUpApprovedAlert } from './Alerts/TopUpApprovedAlert';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';

const mapStateToProps = (state: RootState, { productType }: TProps) => {
    return {
        topUpStatus: getTopUpStatus(state, productType)
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;
type TProps = { accountId: string; productType: TProductType };

const TopUpStatusAlertBoxes = ({
    accountId,
    topUpStatus
}: TProps & PropsFromRedux) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const {
        data: applicationResponse,
        isLoading: applicationLoading,
        isError: applicationError
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey })
    });

    if (
        accountsLoading ||
        accountsError ||
        applicationLoading ||
        applicationError
    )
        return null;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;

    switch (topUpStatus) {
        case 'NOT_YET_TOPUP_ELIGIBLE': {
            const amountToRepayBeforeTopUp =
                account.topup_eligibility!.amount_to_repay_before_topup;

            return (
                <NotYetTopUpEligibleAlert
                    amountToRepayBeforeTopUp={amountToRepayBeforeTopUp}
                />
            );
        }
        case 'TOPUP_PENDING': {
            const requestedAmount =
                applicationResponse.data!.requested_products![0]?.amount;

            const requestedCashAmount =
                applicationResponse.data!.requested_products![0]
                    .requested_cash_amount;

            return (
                <TopUpPendingAlert
                    requestedAmount={requestedAmount}
                    requestedCashAmount={requestedCashAmount}
                />
            );
        }
        case 'TOPUP_ELIGIBLE':
            return <TopUpEligibleAlert />;
        case 'TOPUP_APPROVED':
        case 'PRE_APPROVED': {
            const approvalAmount = account.latest_formal_offer!.available_cash!;
            const timeoutDate = account.latest_formal_offer!.timeout!;
            const interestRate =
                account.latest_formal_offer!.interest_rates![0].rate!;

            return (
                <TopUpApprovedAlert
                    approvalAmount={approvalAmount}
                    timeoutDate={timeoutDate}
                    interestRate={interestRate}
                />
            );
        }
        default:
            return null;
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(TopUpStatusAlertBoxes);
